// Trainer outfits: how the trainer looks on the field, the Trainer page, and
// beside the trainer's name in chat and on the leaderboards. A trainer wears
// one outfit (TrainerDTO.outfit); each outfit names the sprite set drawn for it
// (`${sprite}-${down|up|left|right}-${0|1|2}`, art/sprites/trainer.ts). Under it is
// the trainer's own look, skin tone and hair (shared/looks.ts).
//
// Outfits are looks only: they never change stats, odds or rewards. Some are
// earned through play and the rest are sold in the diamond shop. Bought or
// granted outfits stay on the account: they can't be traded or listed.
import type { TrainerDTO } from './protocol.ts';
import { wornCode } from './looks.ts';
import { REGIONS } from './grounds.ts';
import { dexProgress, DEX_MILESTONES } from './bestiary.ts';

export type OutfitSource =
  | { kind: 'default' }
  /** Earned at this trainer level. */
  | { kind: 'level'; level: number }
  /** Earned for the first Bestiary milestone in any region. */
  | { kind: 'bestiary' }
  /** Sold in the diamond shop. */
  | { kind: 'shop'; price: number }
  /** A day of the season pass (shared/pass.ts). */
  | { kind: 'pass'; season: number }
  /** In a one-time offer: the Starter Pack (real money) or the Founder Box (diamonds). */
  | { kind: 'offer'; offer: 'starter' | 'founder' }
  /** A ranked season's Mythic milestone (shared/ranked.ts). */
  | { kind: 'ranked'; season: number };

export interface Outfit {
  id: string;
  name: string;
  /** Sprite id prefix in the art build. */
  sprite: string;
  desc: string;
  /** Region it is themed on, if any. */
  region?: 'midgard' | 'hellas' | 'yomi';
  source: OutfitSource;
}

/** Diamond price of a shop outfit. */
export const OUTFIT_PRICE = 30;
/** Trainer level that earns the Ranger's Hood. */
export const RANGER_LEVEL = 25;

export const OUTFITS: Outfit[] = [
  { id: 'traveler', name: "Traveler's Cloak", sprite: 'trainer', desc: 'A hooded violet travel cloak over a cream tunic. Every trainer starts in it.', source: { kind: 'default' } },
  { id: 'wayfarer', name: "Wayfarer's Tunic", sprite: 'outfit-wayfarer', desc: 'A sage tunic over linen sleeves, with a satchel on the hip.', source: { kind: 'default' } },
  { id: 'herder', name: "Herder's Jacket", sprite: 'outfit-herder', desc: 'A suede jacket with a fleece collar, denim trousers and fur-lined boots.', source: { kind: 'default' } },
  { id: 'scholar', name: "Scholar's Robe", sprite: 'outfit-scholar', desc: 'A plum robe with an ochre sash, for the well-read trainer.', source: { kind: 'default' } },
  { id: 'ranger', name: "Ranger's Hood", sprite: 'outfit-ranger', desc: 'A moss-green hood and a leather jerkin for long days in the field.', source: { kind: 'level', level: RANGER_LEVEL } },
  { id: 'keeper', name: "Keeper's Coat", sprite: 'outfit-keeper', desc: 'A deep blue coat with a gold-clasped satchel for field notes.', source: { kind: 'bestiary' } },
  { id: 'jarl', name: "Jarl's Furs", sprite: 'outfit-jarl', desc: 'A frost-blue hood rimmed with white fur, over a red wool tunic.', region: 'midgard', source: { kind: 'shop', price: OUTFIT_PRICE } },
  { id: 'hoplite', name: "Hoplite's Crest", sprite: 'outfit-hoplite', desc: 'A bronze helmet with a red horsehair crest and a crimson cloak.', region: 'hellas', source: { kind: 'shop', price: OUTFIT_PRICE } },
  { id: 'ronin', name: "Wanderer's Kasa", sprite: 'outfit-ronin', desc: 'A wide straw hat, dark travelling robes and a red sash.', region: 'yomi', source: { kind: 'shop', price: OUTFIT_PRICE } },
  { id: 'ember', name: 'Ember Mantle', sprite: 'outfit-ember', desc: 'A hood the colour of a banked fire, over a grey tunic.', source: { kind: 'shop', price: OUTFIT_PRICE } },
  { id: 'frostfall', name: 'Frostfall Mantle', sprite: 'outfit-frostfall', desc: 'A rime-white mantle over a deep blue coat, from the first season.', source: { kind: 'pass', season: 1 } },
  { id: 'lantern-robes', name: 'Lantern Robes', sprite: 'outfit-lantern-robes', desc: 'Indigo festival robes with a vermilion sash and a paper lantern on a cord, from the second season.', source: { kind: 'pass', season: 2 } },
  { id: 'starter', name: "Pathfinder's Cape", sprite: 'outfit-pathfinder', desc: 'A travelling cape from the Starter Pack.', source: { kind: 'offer', offer: 'starter' } },
  { id: 'founder', name: "Founder's Regalia", sprite: 'outfit-founder', desc: 'Worn by those who backed the beta, from the Founder Box.', source: { kind: 'offer', offer: 'founder' } },
  { id: 'ranked-s1', name: "Duelist's Laurel", sprite: 'outfit-ranked-s1', desc: 'A laurel crown and a duelist\'s cloak, for reaching Mythic in the first ranked season.', source: { kind: 'ranked', season: 1 } },
];

export const DEFAULT_OUTFIT = 'traveler';
export const OUTFIT: Record<string, Outfit> = Object.fromEntries(OUTFITS.map((o) => [o.id, o]));

/** The outfit a trainer wears; an unknown or missing id falls back to the default. Takes a worn code too ("keeper~s3h2c4", shared/looks.ts). */
export function outfitOf(id: string | undefined | null): Outfit {
  return OUTFIT[(id ?? '').split('~')[0]] ?? OUTFIT[DEFAULT_OUTFIT];
}

/** What a trainer wears, as sent with other trainers for their portraits: the outfit and their look (shared/looks.ts). */
export function wornOf(t: Pick<TrainerDTO, 'outfit' | 'look'> | null | undefined): string {
  return wornCode(outfitOf(t?.outfit).id, t?.look);
}

type OwnerState = Pick<TrainerDTO, 'level' | 'dex' | 'outfits'>;

/** Whether play has earned an outfit (default, level and Bestiary outfits). */
export function earned(o: Outfit, t: OwnerState): boolean {
  switch (o.source.kind) {
    case 'default': return true;
    case 'level': return t.level >= o.source.level;
    case 'bestiary': return REGIONS.some((r) => dexProgress(t.dex ?? {}, undefined, r.id).reached >= 1);
    case 'shop': case 'pass': case 'offer': case 'ranked': return false;
  }
}

/** Whether the trainer may wear an outfit: earned, bought or granted. */
export function ownsOutfit(t: OwnerState, id: string): boolean {
  const o = OUTFIT[id];
  if (!o) return false;
  return earned(o, t) || (t.outfits ?? []).includes(id);
}

/** How an outfit is unlocked, in a few words. */
export function outfitHow(o: Outfit): string {
  switch (o.source.kind) {
    case 'default': return 'Free for every trainer';
    case 'level': return `Reach trainer level ${o.source.level}`;
    case 'bestiary': return `Catch ${DEX_MILESTONES[0].pct}% of any region's species`;
    case 'shop': return `Diamond shop, ${o.source.price} diamonds`;
    case 'pass': return `Season ${o.source.season} pass`;
    case 'offer': return o.source.offer === 'starter' ? 'Starter Pack' : 'Founder Box';
    case 'ranked': return `Ranked season ${o.source.season}: reach Mythic`;
  }
}
